import React, { memo, useCallback } from "react";
import { Link, generatePath, useNavigate } from "react-router";

import { formatUnixTimeToDate } from "@/lib/dayjs/format-date";
import { type ContactListQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";

type ContactSectionProps = {
	sectionTitle: string;
	sectionDetail: string;
};

const ContactSection = memo(({ sectionTitle, sectionDetail }: ContactSectionProps) => {
	return (
		<div>
			<label className="text-sm">{sectionTitle}</label>
			<p className="ml-4 line-clamp-3 whitespace-pre-wrap">{sectionDetail}</p>
		</div>
	);
});

type Props = {
	hotelID: string;
	contactList: ContactListQuery["contactList"]["contacts"];
};

export const ContactList = ({ hotelID, contactList }: Props) => {
	const navigate = useNavigate();

	const chatRoomPagePath = useCallback(
		(contactID: string) => generatePath(PATHS.HOTEL.CONTACT.CHAT_ROOM, { hotelID, contactID }),
		[hotelID],
	);

	const handleClick = useCallback(
		(mouseEvent: React.MouseEvent, contactID: string) => {
			navigate(chatRoomPagePath(contactID));
			mouseEvent.stopPropagation();
		},
		[navigate, chatRoomPagePath],
	);

	// 最終更新日
	const lastUpdateDate = useCallback(
		(idx: number) => String(formatUnixTimeToDate(contactList[idx].messages.at(-1)!.createdAt)),
		[contactList],
	);

	return (
		<div className="flex flex-col-reverse gap-y-7">
			{contactList.map((contact, idx) => {
				const contactID = contact.contactID;

				return (
					<Link
						key={contact.id}
						to={chatRoomPagePath(contactID)}
						className="rounded border border-zinc-200 p-4 hover:bg-zinc-50"
						viewTransition
					>
						<div className="group relative space-y-2">
							<div className="absolute right-0 top-0">
								<Button type="button" variant="outline" onClick={(e) => handleClick(e, contactID)}>
									詳細画面
								</Button>
							</div>

							<div className="flex flex-col gap-y-2 pt-6">
								<ContactSection sectionTitle="件名" sectionDetail={contact.title} />
								<ContactSection sectionTitle="最終更新日時" sectionDetail={lastUpdateDate(idx)} />
								<ContactSection
									sectionTitle="内容"
									sectionDetail={contact.messages.at(-1)?.message ?? ""}
								/>
							</div>
						</div>
					</Link>
				);
			})}
		</div>
	);
};

ContactList.displayName = "ContactList";
